/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import lodashSum from 'lodash.sum';
import MetricsService from 'cio/services/metrics';
import { Periods, BOUNCED_RANGE, SPAM_RANGE, StateRange, getGroupingRanges } from 'cio/utils/metrics/common';
import { DELIVERY_METRICS_MEASURES, JOURNEY_METRICS_MEASURES } from 'cio/utils/metrics/analysis';
import { measureDenominators, MeasureType } from 'cio/utils/metrics/measure';
import TransactionalMessageModel from 'cio/models/transactional-message';
import { Grouping } from 'cio/utils/metrics/resolution';
import { deliveryStatesAndActionsPerType } from 'cio/utils/action-info';
import { TimeRange } from 'cio/utils/time';
import { buildWaiter } from '@ember/test-waiters';
import EnvironmentService from 'cio/services/environment';
import WorkersService, { CIOWorker } from 'cio/services/workers';

let waiter = buildWaiter('transactional-messages:total-worker-waiter');

// Returns a state depending on where a number falls in a range
// Used to display a color indicator for spam or bounced rates
function findState(n: number, { danger, warning }: StateRange) {
  if (n >= danger) {
    return 'danger';
  }

  if (n < danger && n >= warning) {
    return 'warning';
  }

  return 'success';
}

// Returns grouped values by a period (day / week / month) within a given range.
//
// By default, our data is grouped into days. We allow users to group them by
// weeks and months too. To do so:
//
// (a) if the data is raw values, we can just group them and sum each group.
// (b) if the data is rates (percentages), we need the original numerator and
// denominator values so we can group them and derive the final value.
function groupValues(numeratorValues: number[], denominatorValues: number[], timeRange: TimeRange, grouping: Grouping) {
  let isRate = denominatorValues.length > 0;

  if (Grouping.Daily === grouping) {
    return isRate
      ? numeratorValues.map((v, idx) => {
          let denominatorValue = denominatorValues[idx];

          if (denominatorValue == null || denominatorValue === 0) {
            return null;
          }

          return (v / denominatorValue) * 100;
        })
      : numeratorValues;
  } else {
    let ranges = getGroupingRanges(timeRange, Periods.CUSTOM, grouping);
    let numeratorRanges = ranges.map((len: number) => lodashSum(numeratorValues.splice(0, len)));

    if (!isRate) {
      return numeratorRanges;
    } else {
      let denominatorRanges = ranges.map((len: number) => lodashSum(denominatorValues.splice(0, len)));
      return numeratorRanges.map((v, idx) => {
        let denominatorValue = denominatorRanges[idx];

        if (denominatorValue == null || denominatorValue === 0) {
          return null;
        }

        return (v / denominatorValue) * 100;
      });
    }
  }
}

interface TransactionalListDeliveryMetricsArgs {
  messages: TransactionalMessageModel[];
  grouping: Grouping;
  timeRange: TimeRange;
}

export default class TransactionalListDeliveryMetrics extends Component<TransactionalListDeliveryMetricsArgs> {
  @service('metrics') metricsService!: MetricsService;
  @service environment!: EnvironmentService;
  @service workers!: WorkersService;

  @tracked result: {
    delivery_metrics: {
      sent: number[];
      delivered: number[];
      total_sent: number;
      delivered_rate: number;
      bounced_rate: number;
      spammed_rate: number;
    };
  } = {
    delivery_metrics: {
      sent: [],
      delivered: [],
      total_sent: 0,
      delivered_rate: 0,
      bounced_rate: 0,
      spammed_rate: 0,
    },
  };

  @computed('result.delivery_metrics.{sent,delivered_rates}', 'args.{grouping,timeRange}')
  get visibleMetrics() {
    let { timeRange, grouping } = this.args;
    let metrics = this.result.delivery_metrics;

    let sentValues = groupValues(Array.from(metrics.sent ?? []), [], timeRange, grouping);
    let deliveredRates = groupValues(
      Array.from(metrics.delivered ?? []),
      Array.from(metrics.sent ?? []),
      timeRange,
      grouping,
    );

    return [
      {
        name: 'Sent',
        key: 'sent',
        total: metrics.total_sent,
        values: sentValues,
        isRate: false,
      },
      {
        name: 'Delivered',
        key: 'delivered',
        total: metrics.delivered_rate,
        values: deliveredRates,
        isRate: true,
      },
    ];
  }

  @computed('result.delivery_metrics.{bounced_rate,spammed_rate}')
  get summary() {
    let metrics = this.result.delivery_metrics;
    return {
      bouncedRate: {
        value: metrics.bounced_rate,
        state: findState(metrics.bounced_rate * 100, BOUNCED_RANGE),
      },
      spammedRate: {
        value: metrics.spammed_rate,
        state: findState(metrics.spammed_rate * 100, SPAM_RANGE),
      },
    };
  }

  fetchTotalsTask = task(async () => {
    let token = waiter.beginAsync();

    let result = await this.fetchMetricsTask.perform();

    let totalResult = await this.workers.getWorker(CIOWorker.TotalAnalysisMetrics).postMessage([
      result.data,
      this.args.messages.map((m) => `t_${m.id}`),
      ['email', 'push'],
      {
        JOURNEY_METRICS_MEASURES,
        DELIVERY_METRICS_MEASURES,
        DELIVERY_METRICS_MEASURE_DENOMINATORS: measureDenominators(MeasureType.DeliveryMetrics),
        JOURNEY_METRICS_MEASURE_DENOMINATORS: measureDenominators(MeasureType.JourneyMetrics),
        METRICS_PER_ACTION_TYPE: deliveryStatesAndActionsPerType(true),
      },
    ]);

    waiter.endAsync(token);

    this.result = totalResult;
  });

  fetchMetricsTask = task(async () => {
    return this.metricsService.fetchAnalysisMetrics(
      this.args.timeRange,
      Periods.DAYS,
      '',
      [],
      [],
      ['transactional_message'],
      ['email', 'push'],
      {
        excludeZeros: false,
        refreshData: true,
      },
    );
  });

  @computed('environment.domains.@each.ipPool')
  get domainsForTransactional() {
    return (this.environment.domains ?? []).filter((domain) => domain.ipPool === 'transactional');
  }
}
